import { useForm } from '@inertiajs/react'
import type { FormEvent } from 'react'
import FlashMessages from '@/components/FlashMessages'
import { useAccountPath } from '@/lib/useAccountPath'

const ROLES = [
  'Program Manager',
  'L&D Director / Head of L&D',
  'Senior Consultant',
  'Executive Coach',
  'Operations / Admin',
  'Other',
]

const REFERRAL_SOURCES = [
  'Google search',
  'LinkedIn',
  'Colleague or peer',
  'Conference or event',
  'Envisia website',
  'Other',
]

export default function Onboarding() {
  const ap = useAccountPath()

  const { data, setData, post, processing, errors } = useForm({
    name: '',
    job_title: '',
    referral_source: '',
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post(ap('/onboarding'))
  }

  return (
    <div className="bg-base-200 min-h-screen flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Welcome to ImpactCheck</h1>
          <p className="text-base-content/60 mt-2">
            A couple of quick details to tailor your experience.
          </p>
        </div>

        <FlashMessages />

        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <form onSubmit={handleSubmit} className="space-y-5">
              {Object.keys(errors).length > 0 && (
                <div role="alert" className="alert alert-soft alert-error text-sm">
                  <ul className="list-disc list-inside">
                    {Object.values(errors).map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Your name</legend>
                <input
                  type="text"
                  className="input w-full"
                  value={data.name}
                  onChange={(e) => setData('name', e.target.value)}
                  placeholder="e.g. Alex Taylor"
                  maxLength={100}
                  required
                  autoFocus
                />
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">What best describes your role?</legend>
                <select
                  className="select w-full"
                  value={data.job_title}
                  onChange={(e) => setData('job_title', e.target.value)}
                  required
                >
                  <option value="">Select your role</option>
                  {ROLES.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">
                  How did you hear about ImpactCheck?
                  <span className="text-base-content/40 font-normal ms-1">(optional)</span>
                </legend>
                <select
                  className="select w-full"
                  value={data.referral_source}
                  onChange={(e) => setData('referral_source', e.target.value)}
                >
                  <option value="">Select an option</option>
                  {REFERRAL_SOURCES.map((source) => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
              </fieldset>

              <button
                type="submit"
                className="btn btn-primary w-full"
                disabled={processing}
              >
                {processing && <span className="loading loading-spinner loading-sm" />}
                Get started
              </button>
            </form>
          </div>
        </div>

        <p className="text-center text-xs text-base-content/40">
          Powered by{' '}
          <a
            href="https://www.envisialearning.com/private_label"
            target="_blank"
            rel="noopener noreferrer"
            className="link link-hover"
          >
            Envisia Private Label
          </a>
        </p>
      </div>
    </div>
  )
}
